import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import useKpiTriggers from "../hooks/useKpiTriggers";
import TriggersDataContext from "../hooks/TriggersDataContext";
import { getStartingMonth } from "../../../../utils/PeriodCalculationUtils";
import { useParams, useHistory } from "react-router-dom";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import { injectKpiStyles } from "./kpiStyles";
import { useKpiData } from "../hooks/useKpiData";
import KpiPageHeader from "./KpiPageHeader";
import KpiFiltersBar from "./Kpifiltersbar";
import KpiFiltersModal from "./Kpifiltersmodal";
import KpiRow from "./KpiRow";
import { useKpiLocks } from "../../KpiLock/useKpiLocks";
import { hasPendingRequest, lockFor, pendingRequestIds, pendingRequestLocationIds, wasAccessDeclined } from "../../KpiLock/helpers";
import BulkActionBar from "./BulkActionBar";
import AddDataModal from "./AddDataModal";
import ChatModal from "../../components/chatComponent/ChatModal";
import AuditTrailModal from "./AuditTrailModal";
import AssignModal from "./AssignModal";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";

import BulkOwnerAssignModal from "./bulkAction/BulkOwnerAssign/BulkOwnerAssignModal";
import BulkReassignModal from "./bulkAction/Bulkreassignmodal";
import BulkPrefillModal from "./bulkAction/Bulkprefillmodal";
import BulkReminderModal from "./bulkAction/Bulkremindermodal";
import BulkAuditAssignModal from "./bulkAction/BulkAuditAssign/BulkAuditAssignModal";
import ExcelUploader from "./ExcelUploader/ExcelUploader";
import { getLatestFY } from "./answerHistoryPage/components/utils";
import { usePermission } from "../../../../hooks/usePermission";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { PrimaryButton, SecondaryButton } from "../../../../globalComponents/ui/Button";
import KpiRequestModal from "./KpiRequestModal";
import KpiRequestStatusModal from "./KpiRequestStatusModal";
import AccessDenied from "../../../AccessDenied/AccessDenied";

import { questionMatchesSteps, parseTags } from "../../../Settings/Components/TagFilterUtils";

const SESSION_KEY = "quantative_list_state_v2";

// ── Options for assigned-to / assigned-by / auditor filters, derived from
// the loaded list (deduped by id). Shape {id, name} to match the modal's
// multi-select. Hoisted to module scope so it is pure and isn't redefined on render. ──
const buildPeopleOptions = (list = [], getPeople, idKey = "id") => {
    const map = new Map();
    (list || []).forEach((item) => {
        (getPeople(item) || []).forEach((p) => {
            const id = p?.[idKey];
            if (id == null) return;
            const key = String(id);
            if (!map.has(key)) map.set(key, { id: key, name: p.name || p.email || `User ${key}` });
        });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const getFyDateRange = (fyLabel, startingMonth = 4) => {
    if (!fyLabel) return { from: "", to: "" };
    const match = fyLabel.match(/^(\d{4})/);
    if (!match) return { from: "", to: "" };
    const startYear = parseInt(match[1], 10);
    const startMonthStr = String(startingMonth).padStart(2, '0');
    const from = `${startYear}-${startMonthStr}-01`;
    const endYear = startingMonth === 1 ? startYear : startYear + 1;
    const endMonth = startingMonth === 1 ? 12 : startingMonth - 1;
    const endMonthStr = String(endMonth).padStart(2, '0');
    const lastDay = String(new Date(endYear, endMonth, 0).getDate()).padStart(2, '0');
    const to = `${endYear}-${endMonthStr}-${lastDay}`;
    return { from, to };
};

const loadSavedState = () => {
    try {
        return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    } catch {
        return null;
    }
};

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
const QuantativeList = ({
    pageTitle = "Quantitative KPI",
    emissionMode = false,
    scopeFilter = null,
    summarySlot = null,
    ...props
}) => {
    // Don't restore saved state on scope pages — each scope starts clean
    const savedState = scopeFilter ? null : loadSavedState();
    const { moduleName } = useParams();
    const historyRouter = useHistory();

    const { hasPermission, userCapabilities } = usePermission();

    useEffect(() => { injectKpiStyles(); }, []);

    const [sidebarExpanded, setSidebarExpanded] = useState(true);

    const [managementListValue, setManagementListValue] = useState([]);
    const [auditorType, setAuditorType] = useState([]);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const userId = JSON.parse(localStorage.getItem("user_temp_id"));
    const { currentUserId } = useCurrentUser();
    const isExcelUploadEnabled = true;
    const [reportingQuestionsMap, setReportingQuestionsMap] = useState({});

    const fetchFrameworkApi = useCallback(async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
                {},
                { type: "ALL" }
            );
            if (isSuccess) {
                return (data?.data || []).map((item) => item.id);
            }
        } catch (error) {
            console.error("Error fetching framework:", error);
        }
        return [];
    }, []);

    const fetchReportingQuestionsMap = useCallback(async (financialYearId) => {
        if (!financialYearId || !isExcelUploadEnabled) {
            setReportingQuestionsMap({});
            return;
        }

        try {
            const frameworkIds = await fetchFrameworkApi();
            const response = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
                {},
                {
                    financialYearId,
                    frameworkIds,
                },
                "GET"
            );

            if (response?.isSuccess) {
                const nextMap = (response.data?.data || []).reduce((acc, item) => {
                    acc[item.questionId] = item;
                    return acc;
                }, {});
                setReportingQuestionsMap(nextMap);
            } else {
                setReportingQuestionsMap({});
            }
        } catch (error) {
            console.error("Error fetching reporting questions:", error);
            setReportingQuestionsMap({});
        }
    }, [fetchFrameworkApi, isExcelUploadEnabled]);

    const getDesignation = async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getMasterData`,
                {},
                { userId },
                "GET"
            );
            if (isSuccess && isMounted.current) {
                const nonAuditorRoles = data?.data.filter(
                    (role) =>
                        6 == role.id
                );
                const auditorRoles = data?.data.filter(
                    (role) => role.onlyauditor === true
                );
                setManagementListValue(nonAuditorRoles?.reverse());
                setAuditorType(auditorRoles?.reverse());
            }
        } catch (error) {
            console.error("Error fetching designation:", error);
        }
    };

    useEffect(() => {
        getDesignation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [showKpiReqModal, setShowKpiReqModal] = useState(false);
    const [showKpiStatusModal, setShowKpiStatusModal] = useState(false);

    /* ──────────────────────────────────────────────────
       FILTERS
    ────────────────────────────────────────────────── */
    const [search, setSearch] = useState(savedState?.search || "");
    const [moduleFilter, setModuleFilter] = useState(savedState?.moduleFilter || moduleName || "");
    const [frameworkFilter, setFrameworkFilter] = useState(savedState?.frameworkFilter || "");
    const [fyFilter, setFyFilter] = useState(savedState?.fyFilter || "");
    const [expandedId, setExpandedId] = useState(savedState?.expandedId ?? null);

    // ── Cascade tag filter — restore Sets from serialized arrays ──
    const [filterSteps, setFilterSteps] = useState(() => {
        const saved = savedState?.filterSteps || [];
        return saved.map((s) => ({ key: s.key, values: new Set(s.values || []) }));
    });

    const [advFilters, setAdvFilters] = useState(() => {
        const saved = savedState?.advFilters || {};

        return {
            location_ids: Array.isArray(saved.location_ids) ? saved.location_ids : [],
            status: saved.status || "",
            from_date: saved.from_date || "",
            to_date: saved.to_date || "",
            assigned_to_ids: Array.isArray(saved.assigned_to_ids) ? saved.assigned_to_ids : [],
            assigned_by_ids: Array.isArray(saved.assigned_by_ids) ? saved.assigned_by_ids : [],
            // Cascading auditor filter: type → levels (multi) → auditors.
            auditorFilter: (saved.auditorFilter && typeof saved.auditorFilter === "object")
                ? {
                    type: saved.auditorFilter.type || "",
                    levels: Array.isArray(saved.auditorFilter.levels) ? saved.auditorFilter.levels : [],
                    auditorIds: Array.isArray(saved.auditorFilter.auditorIds) ? saved.auditorFilter.auditorIds : [],
                }
                : { type: "", levels: [], auditorIds: [] },
        };
    });

    const [periodSelectorState, setPeriodSelectorState] = useState(
        savedState?.periodSelectorState || { periodType: "", selectedSlot: null, customFrom: "", customTo: "" }
    );

    // Sync period selector → advFilters.from_date / to_date (skip first render to not clobber restored state)
    const periodSyncFirstRender = useRef(true);
    useEffect(() => {
        if (periodSyncFirstRender.current) { periodSyncFirstRender.current = false; return; }
        const { periodType, selectedSlot, customFrom, customTo } = periodSelectorState;
        let from = "";
        let to = "";
        if (periodType === "CUSTOM") {
            from = customFrom;
            to = customTo;
        } else if (selectedSlot) {
            from = selectedSlot.fromDate;
            to = selectedSlot.toDate;
        }
        setAdvFilters(prev => {
            if (prev.from_date === from && prev.to_date === to) return prev;
            return { ...prev, from_date: from, to_date: to };
        });
    }, [periodSelectorState]);

    const advFilterChange = (key, val) => setAdvFilters((p) => ({ ...p, [key]: val }));
    const advFilterCount = Object.entries(advFilters).filter(([k, v]) => {
        if (k === "auditorFilter") return Boolean(v?.type);
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
    }).length;

    const answerFilters = useMemo(() => ({
        location_ids: advFilters.location_ids,
        from_date: advFilters.from_date,
        to_date: advFilters.to_date,
        auditor_type: advFilters.auditorFilter?.type || "",
    }), [advFilters.location_ids, advFilters.from_date, advFilters.to_date, advFilters.auditorFilter?.type]);

    /* ─────────────── hook ─────────────── */
    const {
        kpiList,
        isLoading,
        error,
        financialYears,
        locations,
        modules,
        frameworks,
        selectedFY,
        setSelectedFY,
        saveAnswer,
        refetch,
        answeredQids,
        auditors,
        auditorsError,
    } = useKpiData({
        // Seed the hook with the persisted dropdown FY so the initial load
        // honors the selected year instead of defaulting to the latest.
        financialYearId: fyFilter ? Number(fyFilter) : undefined,
        answerFilters,
        emissionMode,
    });

    const { triggers } = useKpiTriggers(selectedFY?.id);

    useEffect(() => {
        if (!selectedFY?.id) return;
        fetchReportingQuestionsMap(selectedFY.id);
    }, [selectedFY?.id, fetchReportingQuestionsMap]);

    useEffect(() => {
        if (moduleName) setModuleFilter(moduleName);
    }, [moduleName]);

    useEffect(() => {
        if (!selectedFY?.id) return;
        if (!fyFilter) {
            setFyFilter(String(selectedFY.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFY?.id]);


    // ── Persist state to sessionStorage — serialize Sets to arrays ──
    useEffect(() => {
        const state = {
            search,
            moduleFilter,
            fyFilter,
            frameworkFilter,
            advFilters,
            expandedId,
            filterSteps: filterSteps.map((s) => ({ key: s.key, values: [...(s.values || [])] })),
            periodSelectorState,
        };
        const existing = loadSavedState() || {};
        const { scrollTop: _drop, ...rest } = existing;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...rest, ...state }));
    }, [search, moduleFilter, fyFilter, frameworkFilter, advFilters, expandedId, filterSteps, periodSelectorState]);

    const scrollContainerRef = useRef(null);
    const scrollRestored = useRef(false);

    // Publish the table card's visible width as a CSS var so expanded detail
    // panels (inside the horizontally-scrolling area) can size to the viewport
    // and show all their cards without horizontal scrolling.
    const tableCardRef = useRef(null);
    useEffect(() => {
        const el = tableCardRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const apply = () => el.style.setProperty("--kpi-view-width", `${el.clientWidth}px`);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (scrollRestored.current) return;
        if (!expandedId) return;
        if (!kpiList.length) return;

        const doScroll = () => {
            const el = document.querySelector(`[data-kpi-id="${expandedId}"]`);
            if (!el) return false;
            el.scrollIntoView({ block: "center", behavior: "instant" });
            scrollRestored.current = true;
            return true;
        };

        let raf1, raf2, t;
        raf1 = requestAnimationFrame(() => {
            if (!doScroll()) {
                raf2 = requestAnimationFrame(() => {
                    if (!doScroll()) {
                        t = setTimeout(doScroll, 200);
                    }
                });
            }
        });

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kpiList.length]);


    const scopeFilteredList = useMemo(() => {
        if (!scopeFilter) return kpiList;
        const normalised = scopeFilter.toLowerCase().replace(/\s+/g, "");

        const isScopeVal = (v) => {
            const normVal = (v ?? "").toLowerCase().replace(/\s+/g, "");
            return normVal === "scope1" || normVal === "scope2" || normVal === "scope3";
        };

        return kpiList.filter((item) => {
            const combos = parseTags(item.rawTags);
            if (!Array.isArray(combos) || combos.length === 0) return false;

            return combos.some((combo) =>
                Array.isArray(combo) &&
                combo.some((pair) => {
                    const pairKey = pair?.key?.toLowerCase();
                    const pairValue = pair?.value?.toLowerCase().replace(/\s+/g, "");
                    const isScopePair = pairKey === "scope" || isScopeVal(pair?.value);
                    return isScopePair && pairValue === normalised;
                })
            );
        });
    }, [kpiList, scopeFilter]);

    const lockedSteps = useMemo(() => {
        if (!scopeFilter) return [];
        // "SCOPE1" → "scope 1", "SCOPE2" → "scope 2", "SCOPE3" → "scope 3"
        const normalised = scopeFilter.toLowerCase().replace(/\s+/g, "").replace(/scope(\d)/, "scope $1");

        return [{ key: "scope", values: new Set([normalised]) }];
    }, [scopeFilter]);

    const allQuestionsForTagBar = useMemo(() => {
        return scopeFilteredList.map(item => ({
            ...item,
            tags: item.rawTags,
            questionId: item.id,
            kpiId: item.id
        }));
    }, [scopeFilteredList]);

    /* ─────────────── Filtered list using cascade matcher ─────────────── */
    const getKpiStatus = (item) => {
        const isAdmin = Number(currentUserId) === 1;
        const rawHistory = item.quantative_history || [];
        const history = isAdmin
            ? rawHistory
            : rawHistory.filter(ans => Number(ans.createdById) === Number(currentUserId));

        const total = history.length;
        if (total > 0) {
            const accepted = history.filter(ans => ans.status?.toLowerCase() === 'approved').length;
            if (accepted === total) return "completed";
        }
        return "pending";
    };

    const assignedToOptions = useMemo(() => buildPeopleOptions(kpiList, (i) => i.assigned_to, "id"), [kpiList]);
    const assignedByOptions = useMemo(() => buildPeopleOptions(kpiList, (i) => i.assigned_by, "id"), [kpiList]);
    // Auditor filter is type-first: Internal / External → level(s) → auditor.
    // Derived from the FY-wide `auditors` list, NOT kpiList: item.auditors/byKpi
    // are server-filtered by the selected auditor_type, so deriving options from
    // them collapses the dropdown to the picked type and the user can't switch
    // back without a reset. `auditors` stays complete across type changes.
    // Grouped as { INTERNAL: [{userId,name,levels[]}], EXTERNAL: [...] }.
    // Selection values are "type::level::userId" (matched in `filtered`).
    const auditorsByType = useMemo(() => {
        const groups = {};
        (auditors || []).forEach((a) => {
            const uid = a?.userId ?? a?.id;
            const type = a?.auditorType;
            if (uid == null || !type) return;
            groups[type] = groups[type] ?? new Map();
            const key = String(uid);
            if (!groups[type].has(key)) {
                groups[type].set(key, { userId: key, name: a.name || a.email || `User ${uid}`, levels: new Set() });
            }
            groups[type].get(key).levels.add(a.auditLevel ?? 0);
        });
        const out = {};
        Object.keys(groups).forEach((type) => {
            out[type] = [...groups[type].values()]
                .map((g) => ({ ...g, levels: [...g.levels].sort((x, y) => x - y) }))
                .sort((x, y) => x.name.localeCompare(y.name));
        });
        return out;
    }, [auditors]);

    const filtered = useMemo(() => {
        const toStrSet = (arr) => new Set((Array.isArray(arr) ? arr : []).map((x) => String(x ?? "")));
        const toStr = (val) => String(val ?? "");

        const atSet = toStrSet(advFilters.assigned_to_ids);
        const abSet = toStrSet(advFilters.assigned_by_ids);
        const af = advFilters.auditorFilter || {};
        const afLevelSet = toStrSet(af.levels);
        const afAuditorSet = toStrSet(af.auditorIds);

        return scopeFilteredList.filter((item) => {
            if (emissionMode && !answeredQids.has(String(item.id))) return false;
            if (search && !item.kpi_name.toLowerCase().includes(search.toLowerCase())) return false;
            if (moduleFilter && item.module !== moduleFilter) return false;
            if (
                frameworkFilter &&
                (!Array.isArray(item.mapFrameworkIds) ||
                    !item.mapFrameworkIds.includes(String(frameworkFilter)))
            ) return false;

            const itemStatus = getKpiStatus(item);
            if (advFilters.status === "pending" && itemStatus !== "pending") return false;
            if (advFilters.status === "completed" && itemStatus !== "completed") return false;

            // ── Assignee / auditor filters (match any selected) ──
            if (atSet.size > 0 && !(item.assigned_to || []).some((u) => atSet.has(toStr(u.id)))) return false;
            if (abSet.size > 0 && !(item.assigned_by || []).some((u) => abSet.has(toStr(u.id)))) return false;

            // Auditor: type → levels → auditors (each narrows further). Skipped
            // when audit data is unavailable (degraded service → item.auditors
            // null) so a persisted filter can't silently empty the whole list —
            // the degraded-service notice surfaces via auditorsError instead.
            if (af.type && !auditorsError && item.auditors !== null) {
                const ok = (item.auditors || []).some((a) => {
                    if (a.auditorType !== af.type) return false;
                    const aLevel = toStr(a.auditLevel ?? a.level ?? 0);
                    const aUserId = toStr(a.userId ?? a.id);

                    const matchLevel = afLevelSet.size === 0 || afLevelSet.has(aLevel);
                    const matchAuditor = afAuditorSet.size === 0 || afAuditorSet.has(aUserId);
                    return matchLevel && matchAuditor;
                });
                if (!ok) return false;
            }

            // ── Cascade tag filter ──
            const allSteps = [...lockedSteps, ...filterSteps];
            if (!questionMatchesSteps({ ...item, tags: item.rawTags, questionId: item.id }, allSteps)) return false;
            return true;
        });
    }, [kpiList, search, moduleFilter, frameworkFilter, advFilters.status, advFilters.assigned_to_ids, advFilters.assigned_by_ids, advFilters.auditorFilter, filterSteps, lockedSteps, emissionMode, answeredQids, scopeFilteredList, auditorsError]);

    /**
     * Display list that places each derived (formula-based) question's
     * sub-questions directly beneath it, in sub-question order.
     *
     * A derived parent only pulls its sub-questions up when the parent itself
     * survived the filters; otherwise the sub-questions keep their natural
     * position. Sub-questions are resolved from the full `kpiList` rather than
     * `filtered`, so a sub-question hidden by the search term is re-included
     * beneath a matched parent — letting "View Sub Questions" navigate to its row.
     *
     * @returns {Array<object>} the filtered KPI rows re-ordered so each shown
     *   derived parent is immediately followed by its resolvable sub-questions.
     */
    const orderedList = useMemo(() => {
        const byId = new Map(kpiList.map((k) => [k.id, k]));

        const claimedSubIds = new Set();
        const subIdsByParent = new Map();
        filtered.forEach((item) => {
            if (!item.isFormulaBased || !Array.isArray(item.subQuestions)) return;
            const ids = [];
            item.subQuestions.forEach((sub) => {
                if (sub?.id == null || !byId.has(sub.id)) return;
                ids.push(sub.id);
                claimedSubIds.add(sub.id);
            });
            if (ids.length) subIdsByParent.set(item.id, ids);
        });

        const result = [];
        const emitted = new Set();
        filtered.forEach((item) => {
            if (claimedSubIds.has(item.id) || emitted.has(item.id)) return;
            result.push(item);
            emitted.add(item.id);

            const subIds = subIdsByParent.get(item.id);
            if (!subIds) return;
            subIds.forEach((sid) => {
                if (emitted.has(sid)) return;
                result.push(byId.get(sid));
                emitted.add(sid);
            });
        });

        return result;
    }, [filtered, kpiList]);

    const completedCount = orderedList.filter((i) => getKpiStatus(i) === "completed").length;
    const pendingCount = orderedList.length - completedCount;

    const selectedLocations = locations.filter((l) =>
        Array.isArray(advFilters.location_ids) &&
        advFilters.location_ids.includes(l.id)
    );

    /**
     * Locations a deadline is judged against.
     *
     * With no location filter applied a KPI is only fully locked if every
     * location has passed its deadline, so all of them are evaluated rather
     * than none.
     */
    const lockLocationIds = useMemo(
        () => (advFilters.location_ids?.length ? advFilters.location_ids : locations.map((l) => l.id)),
        [advFilters.location_ids, locations]
    );

    const { locks, requests, requestAccess, approveAccess, rejectAccess } = useKpiLocks({
        financialYearId: selectedFY?.id,
        questionIds: orderedList.map((i) => i.id),
        locationIds: lockLocationIds,
        questionnaireType: "QUANT",
    });

    const [requestingAccessId, setRequestingAccessId] = useState(null);

    /**
     * Whether this user decides access requests rather than raising them.
     *
     * Granted by the `due-dates.approve` capability, administered in Settings →
     * Authorization Control, so who approves is configuration rather than code.
     * The capability is location-scoped: a Regional Head holding assigned
     * locations decides only their own sites. The server enforces the same rule,
     * so this only shapes which controls the row offers.
     */
    const canDecideAccess = hasPermission('due-dates.approve');

    const [accessDecisionError, setAccessDecisionError] = useState(null);

    /**
     * Grants an extension on a KPI's outstanding requests.
     *
     * The server re-checks that the caller may decide, so a refusal here is
     * surfaced rather than swallowed — otherwise the row would look unchanged
     * and the administrator would assume the extension was granted.
     * @param {number[]} ids the requests to approve.
     * @param {number} days how many days to reopen entry for.
     */
    const handleApproveAccess = useCallback(async (ids, days, locationIds) => {
        setAccessDecisionError(null);
        const res = await approveAccess(ids, days, locationIds);
        if (!res.ok) {
            setAccessDecisionError(res.message || "Could not approve — only an organisation admin can grant an extension.");
        }
    }, [approveAccess]);

    /**
     * Declines a KPI's outstanding requests, leaving it locked.
     * @param {number[]} ids the requests to reject.
     */
    const handleRejectAccess = useCallback(async (ids, locationIds) => {
        setAccessDecisionError(null);
        const res = await rejectAccess(ids, locationIds);
        if (!res.ok) {
            setAccessDecisionError(res.message || "Could not reject — only an organisation admin can decide access requests.");
        }
    }, [rejectAccess]);

    /**
     * Asks an administrator to reopen a KPI at the locations where it is locked.
     * @param {object} item the KPI row.
     * @param {object} lock the KPI's lock state.
     */
    const handleRequestAccess = useCallback(async (item, lock) => {
        setRequestingAccessId(item.id);
        await requestAccess({
            kpiId: item.id,
            lock,
            fromDate: advFilters.from_date,
            toDate: advFilters.to_date,
        });
        setRequestingAccessId(null);
    }, [requestAccess, advFilters.from_date, advFilters.to_date]);


    const [selectedIds, setSelectedIds] = useState(new Set());
    const allSelected = orderedList.length > 0 && orderedList.every((i) => selectedIds.has(i.id));

    const toggleOne = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    const toggleAll = () =>
        allSelected
            ? setSelectedIds(new Set())
            : setSelectedIds(new Set(orderedList.map((i) => i.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const selectedIdsArray = useMemo(() => [...selectedIds], [selectedIds]);

    /* Derived (formula-based) KPIs are auto-assigned view-only to their sub-question
       owners, so they can't be manually assigned an owner — split them out of the
       bulk owner-assign scope and report how many were skipped. */
    const derivedIdSet = useMemo(
        () => new Set(kpiList.filter((k) => k.isFormulaBased).map((k) => k.id)),
        [kpiList]
    );
    const ownerAssignSelectedIds = useMemo(
        () => selectedIdsArray.filter((id) => !derivedIdSet.has(id)),
        [selectedIdsArray, derivedIdSet]
    );
    const skippedDerivedCount = selectedIdsArray.length - ownerAssignSelectedIds.length;

    const [rangeInput, setRangeInput] = useState("");
    const [rangeError, setRangeError] = useState("");

    const handleRangeClear = () => {
        setRangeInput("");
        setRangeError("");
    };

    const handleRangeApply = () => {
        setRangeError("");
        if (!rangeInput.trim()) {
            setRangeError("Please enter a valid range first.");
            return;
        }

        // Parse range: e.g. "1-3, 5, 8-10"
        const parts = rangeInput.split(",");
        const indices = new Set();
        const maxLen = orderedList.length;

        for (let part of parts) {
            part = part.trim();
            if (!part) continue;

            if (/^\d+$/.test(part)) {
                const idx = parseInt(part, 10);
                if (idx < 1 || idx > maxLen) {
                    setRangeError(`Row ${idx} is out of range (total rows: ${maxLen}).`);
                    return;
                }
                indices.add(idx);
            } else if (/^\d+-\d+$/.test(part)) {
                const [startStr, endStr] = part.split("-");
                const start = parseInt(startStr, 10);
                const end = parseInt(endStr, 10);
                if (start < 1 || end < 1 || start > maxLen || end > maxLen) {
                    setRangeError(`Range ${part} is out of range (total rows: ${maxLen}).`);
                    return;
                }
                if (start > end) {
                    setRangeError(`Invalid range: ${part}. Start row must be less than or equal to end row.`);
                    return;
                }
                for (let i = start; i <= end; i++) {
                    indices.add(i);
                }
            } else {
                setRangeError(`Invalid format: "${part}". Use numbers and ranges, e.g. "1-3, 5".`);
                return;
            }
        }

        if (indices.size === 0) {
            setRangeError("No valid rows matched.");
            return;
        }

        const matchedIds = [...indices].map(idx => orderedList[idx - 1].id);
        setSelectedIds(new Set(matchedIds));
        setRangeError("");
    };

    /* ── Per-row modal state ── */
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [modal, setModal] = useState({ type: null, item: null });
    const openModal = (type, item) => setModal({ type, item });
    const closeModal = () => setModal({ type: null, item: null });

    const [bulkModal, setBulkModal] = useState(null);
    const closeBulkModal = useCallback(() => setBulkModal(null), []);

    const handleBulkAction = useCallback((actionKey) => {
        setBulkModal(actionKey);
    }, []);

    /* ── Helpers for active filter step count ── */
    const activeFilterStepCount = filterSteps.filter((s) => s.values?.size > 0).length;
    const hasAnyFilter =
        search ||
        moduleFilter ||
        fyFilter ||
        frameworkFilter ||
        advFilterCount > 0 ||
        activeFilterStepCount > 0 ||
        rangeInput;

    /* ── Period selector handlers ── */
    const handlePeriodTypeChange = useCallback((type) => {
        setPeriodSelectorState(prev => ({ ...prev, periodType: type, selectedSlot: null }));
    }, []);

    const handleSlotChange = useCallback((slot) => {
        setPeriodSelectorState(prev => ({ ...prev, selectedSlot: slot }));
    }, []);

    const handleCustomFromChange = useCallback((val) => {
        setPeriodSelectorState(prev => ({ ...prev, customFrom: val }));
    }, []);

    const handleCustomToChange = useCallback((val) => {
        setPeriodSelectorState(prev => ({ ...prev, customTo: val }));
    }, []);



    /* ── Clear ALL filters including cascade tag steps ── */
    const clearAllFilters = () => {
        setSearch("");
        setModuleFilter("");

        const latestFy = getLatestFY(financialYears);
        const latestFyId = latestFy ? String(latestFy.id) : "";
        setFyFilter(latestFyId);
        if (latestFy) {
            setSelectedFY({ id: latestFy.id, name: latestFy.financial_year_value || latestFy.name, financial_year_value: latestFy.financial_year_value });
        }

        setFrameworkFilter("");
        setAdvFilters({ location_ids: [], status: "", from_date: "", to_date: "", assigned_to_ids: [], assigned_by_ids: [], auditorFilter: { type: "", levels: [], auditorIds: [] } });
        setPeriodSelectorState({ periodType: "", selectedSlot: null, customFrom: "", customTo: "" });
        setFilterSteps([]);
        handleRangeClear();
        sessionStorage.removeItem(SESSION_KEY);
    };

    /* ── Handle FY change ── */
    const handleFyChange = (v) => {
        setFyFilter(v);
        if (v) {
            const fy = financialYears.find((f) => String(f.id) === String(v));
            if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value || fy.name, financial_year_value: fy.financial_year_value });
        }
    };

    const handleOpenHistory = useCallback((item) => {
        const statePayload = {
            kpiName: item.kpi_name,
            financialYearId: selectedFY?.id,
            financialYearName: selectedFY?.name,
            locations: locations,
            fromDate: advFilters.from_date ?? "",
            toDate: advFilters.to_date ?? "",
            location_ids: advFilters.location_ids ?? [],
            status: advFilters.status ?? "",
            module: item.module,
            assignedTo: item.assigned_to,
            unitSetting: item.unitSetting ?? null,
            emissionMode: emissionMode,
            assign_raw_list: item.assign_raw_list ?? [],
            kpiType: item.kpi_type ?? null,
            variablesJson: item.variablesJson,
            isFormulaBased: item.isFormulaBased ?? false,
            subQuestions: Array.isArray(item.subQuestions) ? item.subQuestions : [],
        };
        sessionStorage.setItem(`ahp_state_${item.id}`, JSON.stringify(statePayload));
        historyRouter.push(`/data-management/quantitative/${item.id}`, statePayload);
    }, [historyRouter, selectedFY, locations, advFilters]);

    /* ─────────────────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────────────────── */

    /* ─────────────── Column header helper ─────────────── */
    const ColLabel = ({ children, width, flex, minWidth, align = "left" }) => (
        <div style={{
            ...(width ? { width, flexShrink: 0 } : {}),
            ...(flex ? { flex } : {}),
            ...(minWidth ? { minWidth } : {}),
            padding: "0 10px",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
            textAlign: align,
        }}>
            {children}
        </div>
    );

    const requiredPermission = (emissionMode && scopeFilter)
        ? `scope-${scopeFilter.replace(/[^0-9]/g, "")}.view`
        : "quantitative-data.view";

    if (!hasPermission(requiredPermission) && userCapabilities.length > 0) {
        return <AccessDenied {...props} />;
    }

    return (
        <TriggersDataContext.Provider value={{ triggers, fromDate: advFilters.from_date, toDate: advFilters.to_date }}>
            <div
                className="kpi-root"
                style={{
                    display: "flex",
                    background: "var(--surface-2)",
                    minHeight: "100vh",
                    fontFamily: "var(--font-sans)",
                }}
            >
                <div
                    style={{
                        flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
                        position: "sticky",
                        top: 0,
                        height: "100vh",
                        background: "var(--surface)",
                        borderRight: "1px solid var(--rule)",
                        zIndex: 100,
                        transition: "flex 0.2s ease",
                    }}
                >
                    <Sidebar
                        dataFromParent={props.location?.pathname || "/dashboard"}
                        onSidebarToggle={(isOpen) => setSidebarExpanded(isOpen)}
                    />
                </div>

                <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
                    <Header />

                    <KpiPageHeader
                        pageTitle={pageTitle}
                        selectedFY={selectedFY}
                        totalCount={orderedList.length}
                        completedCount={completedCount}
                        pendingCount={pendingCount}
                        selectedLocation={selectedLocations}
                        onClearLocation={() => advFilterChange("location_id", "")}
                        onOpenFilters={() => setShowFiltersModal(true)}
                        advFilterCount={advFilterCount}
                        rightSlot={
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                {hasPermission("kpi.view") && (
                                    <button
                                        onClick={() => historyRouter.push("/quick-view")}
                                        style={{
                                            height: 38, padding: "0 16px",
                                            border: "none", borderRadius: 8,
                                            background: "var(--accent, #3f88a5)", color: "#fff",
                                            fontSize: 13, fontWeight: 700, cursor: "pointer",
                                        }}
                                    >
                                        Quick View
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowKpiReqModal(true)}
                                    style={{
                                        height: 38, padding: "0 16px",
                                        border: "1.5px solid var(--accent, #3f88a5)", borderRadius: 8,
                                        background: "transparent", color: "var(--accent, #3f88a5)",
                                        fontSize: 13, fontWeight: 700, cursor: "pointer",
                                    }}
                                >
                                    Request KPI
                                </button>
                                <button
                                    onClick={() => setShowKpiStatusModal(true)}
                                    style={{
                                        height: 38, padding: "0 16px",
                                        border: "1.5px solid var(--rule, #e5e7eb)", borderRadius: 8,
                                        background: "#fff", color: "var(--ink-3, #475569)",
                                        fontSize: 13, fontWeight: 600, cursor: "pointer",
                                    }}
                                >
                                    Request Status
                                </button>
                                {hasPermission("excel.upload") && (
                                    isExcelUploadEnabled ? (
                                        <ExcelUploader
                                            financialYearId={selectedFY?.id}
                                            financialYear={financialYears}
                                            reportingQuestionsMap={reportingQuestionsMap}
                                        />
                                    ) : (
                                        <PrimaryButton type="button">
                                            Upload
                                        </PrimaryButton>
                                    )
                                )}
                            </div>
                        }
                        summarySlot
                    />

                    <KpiFiltersBar
                        search={search}
                        onSearchChange={setSearch}
                        moduleFilter={moduleFilter}
                        onModuleChange={setModuleFilter}
                        fyFilter={fyFilter}
                        onFyChange={handleFyChange}
                        frameworkFilter={frameworkFilter}
                        onFrameworkChange={setFrameworkFilter}
                        financialYears={financialYears}
                        modules={modules}
                        frameworks={frameworks}
                        locations={locations}
                        advFilterCount={advFilterCount}
                        onOpenAdvanced={() => setShowFiltersModal(true)}
                        onClearAll={clearAllFilters}
                        // ── Cascade tag filter props ──────────────────────────
                        kpiList={allQuestionsForTagBar}
                        filterSteps={filterSteps}
                        onFilterStepsChange={setFilterSteps}
                        lockedSteps={lockedSteps}
                        // ── Range Selection props ─────────────────────────────
                        rangeInput={rangeInput}
                        rangeError={rangeError}
                        onRangeInputChange={setRangeInput}
                        onRangeClear={handleRangeClear}
                        onRangeApply={handleRangeApply}
                    />

                    {summarySlot && (
                        <div style={{ padding: "12px 24px 4px" }}>
                            {summarySlot}
                        </div>
                    )}

                    <BulkActionBar
                        count={selectedIds.size}
                        selectedIds={selectedIdsArray}
                        onAction={handleBulkAction}
                        onDeselect={clearSelection}
                        hasPermission={hasPermission}
                        showPrefill={false}
                    />

                    <div
                        ref={scrollContainerRef}
                        style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}
                    >
                        {/* ── Active filter indicator chips ── */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingLeft: 4 }}>
                            {selectedIds.size > 0 && (
                                <>
                                    <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                    <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>{selectedIds.size} selected</span>
                                </>
                            )}
                            {advFilters.location_ids?.length > 0 && (
                                <>
                                    <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                    <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                        📍 {selectedLocations.length} location{selectedLocations.length > 1 ? "s" : ""} selected
                                    </span>
                                </>
                            )}
                            {(advFilters.from_date || advFilters.to_date) && (
                                <>
                                    <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                    <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                        📅 {advFilters.from_date || "…"} → {advFilters.to_date || "…"}
                                    </span>
                                </>
                            )}
                            {/* ── Active cascade tag filter chip ── */}
                            {activeFilterStepCount > 0 && (
                                <>
                                    <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                    <span style={{ fontSize: 11.5, color: "#4338ca", fontWeight: 600 }}>
                                        🏷️ {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                    </span>
                                </>
                            )}
                        </div>

                        <div ref={tableCardRef} style={{
                            background: "var(--surface)",
                            borderRadius: 12,
                            border: "1px solid var(--rule)",
                            // The card is the single scroll region (both axes): rows scroll
                            // vertically INSIDE it while the sticky column header stays pinned,
                            // and wide content scrolls horizontally — all contained in the card.
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)",
                        }}>
                            <div style={{ minWidth: 1290 }}>
                                {/* ── Column headers ── */}
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "10px 0",
                                    background: "var(--surface-3)",
                                    borderBottom: "2px solid #e5e8ed",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10,
                                }}>
                                    <div style={{ width: 46, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                        <input type="checkbox" className="kpi-checkbox" checked={allSelected} onChange={toggleAll} title={allSelected ? "Deselect all" : "Select all"} disabled={emissionMode} />
                                    </div>
                                    <div style={{ width: 32, flexShrink: 0 }} />
                                    <ColLabel flex={1} minWidth={350}>KPI</ColLabel>
                                    <ColLabel width={150}>Assigned To</ColLabel>
                                    <ColLabel width={130}>Assigned By</ColLabel>
                                    <ColLabel width={150}>Auditors</ColLabel>
                                    <ColLabel width={120}>Status</ColLabel>
                                    <ColLabel width={120}> {emissionMode ? "Emission (FY)" : "Value"}</ColLabel>
                                    {!emissionMode && <ColLabel width={120}>Draft</ColLabel>}
                                    <div style={{ width: 44, flexShrink: 0 }} />
                                </div>

                                {/* ── Loading skeleton ── */}
                                {isLoading && (
                                    <div>
                                        {[1, 2, 3, 4, 5, 6].map((i) => (
                                            <div key={i} style={{ display: "flex", alignItems: "center", padding: "18px 20px", borderBottom: "1px solid var(--rule-2)", gap: 14, opacity: 1 - i * 0.1 }}>
                                                <div style={{ width: 16, height: 16, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                                <div style={{ width: 20, height: 12, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                                                    <div className="kpi-shimmer" style={{ height: 13, borderRadius: 4, width: `${60 + (i * 11) % 35}%`, background: "#eaecf0" }} />
                                                    <div style={{ height: 10, borderRadius: 4, background: "#f1f3f6", width: "28%" }} />
                                                </div>
                                                {[130, 110, 100, 88].map((w) => (
                                                    <div key={w} style={{ width: w, height: 26, borderRadius: 6, background: "#eaecf0", flexShrink: 0 }} />
                                                ))}
                                                <div style={{ width: 68, height: 22, borderRadius: 20, background: "#eaecf0", flexShrink: 0 }} />
                                                <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eaecf0", flexShrink: 0 }} />
                                            </div>
                                        ))}
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 24px", gap: 10, color: "var(--ink-4)", fontSize: 12, fontFamily: "var(--font-sans)", borderTop: "1px solid var(--rule-2)" }}>
                                            <div className="kpi-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                                            Loading KPIs…
                                        </div>
                                    </div>
                                )}

                                {/* ── Error state ── */}
                                {!isLoading && error && (
                                    <div style={{ padding: "52px 24px", textAlign: "center", fontSize: 13 }}>
                                        <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
                                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14, color: "var(--red)" }}>Failed to load data</div>
                                        <div style={{ color: "var(--ink-4)", fontSize: 13, marginBottom: 20 }}>{error}</div>
                                        <SecondaryButton onClick={() => refetch()}>↻ Retry</SecondaryButton>
                                    </div>
                                )}

                                {/* ── Empty state ── */}
                                {!isLoading && !error && orderedList.length === 0 && (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "68px 24px", gap: 10 }}>
                                        <span style={{ fontSize: 40, opacity: 0.35 }}>📊</span>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink-2)", fontFamily: "var(--font-sans)" }}>No Data Found</div>
                                        <div style={{ fontSize: 12.5, color: "var(--ink-4)" }}>
                                            {(advFilters.from_date || advFilters.to_date)
                                                ? "No data found for that date range."
                                                : activeFilterStepCount > 0
                                                    ? "No KPIs match the selected tags. Try selecting different tags or clear the tag filter."
                                                    : "Try adjusting the filters or search term."}
                                        </div>
                                        {hasAnyFilter && (
                                            <PrimaryButton className="kpi-btn-secondary" onClick={clearAllFilters} style={{ marginTop: 8 }}>Clear all filters</PrimaryButton>
                                        )}
                                    </div>
                                )}

                                {accessDecisionError && (
                                    <div
                                        role="alert"
                                        style={{ margin: "0 20px 12px", padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: 12.5 }}
                                    >
                                        {accessDecisionError}
                                    </div>
                                )}

                                {/* ── KPI rows ── */}
                                {!isLoading && !error && orderedList.map((item, index) => (
                                    <KpiRow
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        selected={selectedIds.has(item.id)}
                                        onToggle={() => toggleOne(item.id)}
                                        searchQuery={search}
                                        expanded={expandedId === item.id}

                                        onExpand={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                                        onAdd={(i) => hasPermission("quantitative-data.create") && openModal("add", i)}
                                        onHistory={handleOpenHistory}
                                        onChat={(i) => openModal("chat", i)}
                                        onAudit={(i) => openModal("audit", i)}
                                        onAssign={(i) => hasPermission("data-owner.assign") && openModal("assign", i)}
                                        isEven={index % 2 === 0}
                                        emissionMode={emissionMode}
                                        hasPermission={hasPermission}
                                        lock={lockFor(locks, item.id)}
                                        accessRequested={hasPendingRequest(requests, item.id, null)}
                                        accessDeclined={wasAccessDeclined(requests, item.id, null)}
                                        requestingAccess={requestingAccessId === item.id}
                                        onRequestAccess={handleRequestAccess}
                                        canDecideAccess={canDecideAccess}
                                        pendingRequestIds={pendingRequestIds(requests, item.id)}
                                        pendingRequestLocationIds={pendingRequestLocationIds(requests, item.id)}
                                        onApproveAccess={handleApproveAccess}
                                        onRejectAccess={handleRejectAccess}
                                    />
                                ))}

                                {/* ── Footer summary ── */}
                                {!isLoading && !error && orderedList.length > 0 && (
                                    <div style={{
                                        padding: "11px 20px",
                                        borderTop: "1px solid var(--rule-2)",
                                        background: "#f8f9fb",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}>
                                        <span style={{ fontSize: 11.5, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
                                            Showing {orderedList.length} of {kpiList.length} KPIs
                                            {activeFilterStepCount > 0 && (
                                                <span style={{ marginLeft: 6, color: "#4338ca" }}>
                                                    · {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                                </span>
                                            )}
                                        </span>
                                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--green)", fontWeight: 600 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", display: "inline-block" }} />
                                                {completedCount} done
                                            </span>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--amber)", fontWeight: 600 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--amber)", display: "inline-block" }} />
                                                {pendingCount} pending
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>

                {/* ── Modals ── */}
                <KpiFiltersModal
                    show={showFiltersModal}
                    onHide={() => setShowFiltersModal(false)}
                    filters={advFilters}
                    onApply={(nextFilters, nextPeriodState) => {
                        setAdvFilters(nextFilters);
                        setPeriodSelectorState(nextPeriodState);
                    }}
                    onReset={() => {
                        setAdvFilters({ location_ids: [], status: "", from_date: "", to_date: "", assigned_to_ids: [], assigned_by_ids: [], auditorFilter: { type: "", levels: [], auditorIds: [] } });
                        setPeriodSelectorState({ periodType: "", selectedSlot: null, customFrom: "", customTo: "" });
                    }}
                    locations={locations}
                    assignedToOptions={assignedToOptions}
                    assignedByOptions={assignedByOptions}
                    auditorsByType={auditorsByType}
                    auditorsError={auditorsError}
                    isLoading={isLoading}
                    fyLabel={selectedFY?.financial_year_value || selectedFY?.name || ""}
                    startingMonth={getStartingMonth()}
                    periodSelectorState={periodSelectorState}
                />

                <AddDataModal
                    show={modal.type === "add"}
                    onHide={closeModal}
                    item={modal.item}
                    financialYearId={selectedFY?.id}
                    fyLabel={selectedFY?.financial_year_value || selectedFY?.name || ""}
                    startingMonth={getStartingMonth()}
                    locations={locations}
                    saveAnswer={saveAnswer}
                    onSave={(item, data) => {
                        console.log("[AddData] saved", item.id, data);
                        refetch();
                    }}
                />

                <ChatModal
                    show={modal.type === "chat"}
                    onHide={closeModal}
                    item={modal.item}
                    questionId={modal.item?.id}
                    financialYearId={modal.item?.financial_year_id}
                    sourceId={modal.item?.location_id}
                    fromDate={modal.item?.from_date}
                    toDate={modal.item?.to_date}
                    currentUserId={JSON.parse(localStorage.getItem("currentUser") || "null")?.id ?? null}
                    userForRole={JSON.parse(localStorage.getItem("currentUser") || "null") ?? {}}
                    participants={[]}
                    initialMessages={[]}
                    onSend={(msg, recipients) => console.log("[Chat] send", msg, recipients)}
                />

                <AuditTrailModal
                    show={modal.type === "audit"}
                    onHide={closeModal}
                    item={modal.item}
                    financialYearId={selectedFY?.id}
                />

                <AssignModal
                    show={modal.type === "assign"}
                    onHide={closeModal}
                    item={modal.item}
                    assignDetailsFromApi={modal.item?.assign_raw || null}
                    sources={locations}
                />

                <BulkOwnerAssignModal
                    show={bulkModal === "assign"}
                    onHide={closeBulkModal}
                    selectedIds={ownerAssignSelectedIds}
                    financialYearId={selectedFY?.id}
                    locations={locations}
                    selectedKpis={kpiList.filter(kpi => selectedIds.has(kpi.id) && !kpi.isFormulaBased)}
                    allQuestions={allQuestionsForTagBar}
                    skippedDerivedCount={skippedDerivedCount}
                    onSuccess={() => { clearSelection(); refetch(); }}
                />

                <BulkReassignModal
                    show={bulkModal === "reassign"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIdsArray}
                    financialYearId={selectedFY?.id}
                    managementListValue={managementListValue}
                    onSuccess={() => { clearSelection(); refetch(); }}
                />

                <BulkPrefillModal
                    show={bulkModal === "prefill"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIdsArray}
                    financialYears={financialYears}
                    selectedFinancialYearId={selectedFY?.id}
                    onSuccess={() => { clearSelection(); refetch(); }}
                />

                <BulkReminderModal
                    show={bulkModal === "reminder"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIdsArray}
                    onSuccess={() => clearSelection()}
                />

                <BulkAuditAssignModal
                    show={bulkModal === "auditAssign"}
                    onHide={closeBulkModal}
                    selectedIds={selectedIdsArray}
                    financialYearId={selectedFY?.id}
                    locationId={advFilters.location_id || null}
                    companyId={JSON.parse(localStorage.getItem("currentUser") || "null")?.companyId ?? null}
                    managementListValue={auditorType}
                    reportingQuestions={allQuestionsForTagBar}
                    onSuccess={() => { clearSelection(); refetch(); }}
                />

                <KpiRequestModal
                    show={showKpiReqModal}
                    onHide={() => setShowKpiReqModal(false)}
                />

                <KpiRequestStatusModal
                    show={showKpiStatusModal}
                    onHide={() => setShowKpiStatusModal(false)}
                    allowedTypes={["KPI"]}
                />
            </div>
        </TriggersDataContext.Provider>
    );
};

export default QuantativeList;
